import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// API
import { useTurmaService } from '../../services/turmaServices';
import { useEmpresaService } from '../../services/empresaServices';
import { useParticipanteService } from '../../services/participanteServices';

import { useTrilhaService } from '../../services/trilhaServices';
import { useModuloService } from '../../services/moduloServices';
import { useModuloSecaoService } from '../../services/moduloSecaoServices';
import { useModuloConteudoService } from '../../services/moduloConteudoServices';
import { useTesteService } from '../../services/testeServices';

// Constantes
import { URLS } from '../../constants/urls';

// Estilos
import styles from './eadtrilhapage.module.css';

// ✅ ATIVE AQUI
const DEBUG = true;

const debug = {
  log: (...args) => { if (DEBUG) console.log('[EadTrilhaPage]', ...args); },
  warn: (...args) => { if (DEBUG) console.warn('[EadTrilhaPage]', ...args); },
  error: (...args) => { if (DEBUG) console.error('[EadTrilhaPage]', ...args); },
  table: (obj) => { if (DEBUG && console.table) console.table(obj); },
  group: (...args) => { if (DEBUG && console.group) console.group('[EadTrilhaPage]', ...args); },
  groupCollapsed: (...args) => { if (DEBUG && console.groupCollapsed) console.groupCollapsed('[EadTrilhaPage]', ...args); },
  groupEnd: () => { if (DEBUG && console.groupEnd) console.groupEnd(); },
  time: (label) => { if (DEBUG && console.time) console.time(`[EadTrilhaPage] ${label}`); },
  timeEnd: (label) => { if (DEBUG && console.timeEnd) console.timeEnd(`[EadTrilhaPage] ${label}`); },
};

const safeArray = (v) => (Array.isArray(v) ? v : []);
const normalizeText = (v) => (v === null || typeof v === 'undefined' ? '' : String(v));

function isAbsoluteLike(url) {
  const u = String(url || '').trim();
  return (
    /^https?:\/\//i.test(u) ||
    /^data:/i.test(u) ||
    /^blob:/i.test(u) ||
    /^\/\//.test(u)
  );
}

function joinBaseAndPath(base, path) {
  const b = String(base || '');
  const p = String(path || '');
  if (!b) return p;
  if (!p) return '';

  const b2 = b.endsWith('/') ? b.slice(0, -1) : b;
  const p2 = p.startsWith('/') ? p.slice(1) : p;
  return `${b2}/${p2}`;
}

// Regra: se vier relativo, prefixa com URLS.IMG_PATH
function toAssetUrl(maybePath) {
  const raw = String(maybePath || '').trim();
  if (!raw) return '';

  // Já é absoluta (http, data, blob etc)
  if (isAbsoluteLike(raw)) return raw;

  // Já começa com o prefixo configurado
  const base = String(URLS?.IMG_PATH || '').trim();
  if (base && raw.startsWith(base)) return raw;

  // Relativo => prefixa
  return joinBaseAndPath(base, raw);
}

function looksLikePdf(pathOrUrl) {
  const v = String(pathOrUrl || '').toLowerCase();
  return v.endsWith('.pdf') || v.includes('/reports/') || v.includes('reports/');
}

function PdfViewerModal({ open, title, url, onClose }) {
  // Fecha com ESC
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.pdfOverlay} onMouseDown={onClose}>
      <div
        className={styles.pdfModal}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.pdfHeader}>
          <div className={styles.pdfTitle}>
            {title || 'Visualizador de PDF'}
          </div>

          <div className={styles.pdfActions}>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className={styles.pdfActionBtn}
              title="Abrir em nova aba"
            >
              Abrir
            </a>

            <a
              href={url}
              download
              className={styles.pdfActionBtnOutline}
              title="Baixar PDF"
            >
              Baixar
            </a>

            <button
              type="button"
              className={styles.pdfCloseBtn}
              onClick={onClose}
              title="Fechar (ESC)"
            >
              ✕
            </button>
          </div>
        </div>

        <div className={styles.pdfBody}>
          <iframe
            title={title || 'pdf'}
            src={url}
            className={styles.pdfFrame}
          />
        </div>
      </div>
    </div>
  );
}

function EadTrilhaPage() {
  const { idTurma } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { logout, usuario } = useAuth();
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();

  const { obterTurmaPorId, associarParticipanteATurma } = useTurmaService();
  const { obterEmpresaPorId } = useEmpresaService();
  const { listarParticipantesPorTurma } = useParticipanteService();

  const { obterTrilhaPorId } = useTrilhaService();
  const { listarModulosPorTrilha } = useModuloService();
  const { listarSecoesPorModulo } = useModuloSecaoService();
  const { listarConteudosPorSecao } = useModuloConteudoService();
  const { obterTestePorId } = useTesteService();

  const [empresa, setEmpresa] = useState(null);
  const [turma, setTurma] = useState(null);

  const [trilha, setTrilha] = useState(null);
  const [modulos, setModulos] = useState([]);

  const [selectedModuloId, setSelectedModuloId] = useState(null);

  // Cache de detalhes por módulo (evita refetch ao voltar/abrir de novo)
  const [detalhesCache, setDetalhesCache] = useState({});

  // PDF viewer
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: '',
    title: ''
  });

  const openPdf = (url, title) => {
    if (!url) return;
    debug.log('openPdf ->', { url, title });
    setPdfViewer({ open: true, url, title: title || 'PDF' });
  };

  const closePdf = () => setPdfViewer({ open: false, url: '', title: '' });

  // 🔎 contador local de loader (ajuda a detectar “vazamento”)
  const loaderCountRef = useRef(0);
  const showL = (msg) => {
    loaderCountRef.current += 1;
    debug.log(`showLoader(${loaderCountRef.current}) ->`, msg);
    showLoader(msg);
  };
  const hideL = () => {
    loaderCountRef.current = Math.max(0, loaderCountRef.current - 1);
    debug.log(`hideLoader(${loaderCountRef.current})`);
    hideLoader();
  };

  const selectedModulo = useMemo(() => {
    if (!selectedModuloId) return null;
    return modulos.find((m) => String(m.id) === String(selectedModuloId)) || null;
  }, [selectedModuloId, modulos]);

  const detalhesSelecionado = useMemo(() => {
    if (!selectedModuloId) return null;
    return detalhesCache[selectedModuloId] || null;
  }, [selectedModuloId, detalhesCache]);

  // ===== Helpers =====
  const sortByOrdem = (a, b) => {
    const oa = Number(a?.ordem ?? 0);
    const ob = Number(b?.ordem ?? 0);
    if (oa !== ob) return oa - ob;
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
  };

  // ✅ Redireciona para login mantendo “from”
  const redirectToLogin = (reason = '') => {
    debug.groupCollapsed('redirectToLogin');
    debug.log('reason:', reason);
    debug.log('idTurma:', idTurma);
    debug.log('location:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    });
    debug.groupEnd();

    navigate(
      `/participante/login?turma=${encodeURIComponent(idTurma || '')}&dest=ead`,
      { replace: true, state: { from: location } }
    );
  };

  // YouTube: converte qualquer URL (watch/youtu.be/shorts/embed) para embed
  const toYouTubeEmbedUrl = (inputUrl) => {
    if (!inputUrl) return null;

    try {
      const u = new URL(inputUrl);

      if (u.hostname.includes('youtu.be')) {
        const videoId = u.pathname.replace('/', '').trim();
        if (!videoId) return null;
        const list = u.searchParams.get('list');
        return `https://www.youtube.com/embed/${videoId}${list ? `?list=${list}` : ''}`;
      }

      if (u.hostname.includes('youtube.com')) {
        if (u.pathname === '/watch') {
          const videoId = u.searchParams.get('v');
          if (!videoId) return null;
          const list = u.searchParams.get('list');
          return `https://www.youtube.com/embed/${videoId}${list ? `?list=${list}` : ''}`;
        }

        if (u.pathname.startsWith('/shorts/')) {
          const videoId = u.pathname.split('/shorts/')[1]?.split('/')[0]?.trim();
          if (!videoId) return null;
          return `https://www.youtube.com/embed/${videoId}`;
        }

        if (u.pathname.startsWith('/embed/')) return inputUrl;
      }

      return null;
    } catch (e) {
      debug.warn('toYouTubeEmbedUrl: URL inválida:', inputUrl, e);
      return null;
    }
  };

  // ===== LOGS DE MOUNT / PARAMS =====
  useEffect(() => {
    debug.groupCollapsed('mount');
    debug.log('params.idTurma:', idTurma);
    debug.log('usuario:', usuario);
    debug.log('URLS.IMG_PATH:', URLS?.IMG_PATH);
    debug.log('location:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    });
    try {
      debug.log('idTurma decodeURIComponent:', decodeURIComponent(idTurma || ''));
    } catch (e) {
      debug.warn('decodeURIComponent falhou:', e);
    }
    debug.groupEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== 1) Fluxo principal =====
  useEffect(() => {
    let cancelled = false;

    const carregar = async () => {
      debug.groupCollapsed('carregar (fluxo principal)');
      debug.log('start -> idTurma:', idTurma, 'usuario.id:', usuario?.id);

      if (!idTurma) {
        debug.warn('early return: idTurma vazio');
        debug.groupEnd();
        return;
      }

      if (!usuario?.id) {
        debug.warn('early return: usuário não logado');
        showToast('Faça login para acessar a trilha.', 3500, 'error');
        redirectToLogin('fluxo principal: usuario.id ausente');
        debug.groupEnd();
        return;
      }

      showL('Buscando dados da turma...');
      debug.time('fluxo principal total');

      try {
        // 1) Turma
        debug.time('obterTurmaPorId');
        debug.log('REQ obterTurmaPorId ->', { idTurma });
        const turmaRes = await obterTurmaPorId(idTurma);
        debug.timeEnd('obterTurmaPorId');
        debug.log('RES obterTurmaPorId ->', turmaRes);

        if (!turmaRes?.success) {
          showToast(turmaRes?.message || 'Erro ao carregar dados da turma.', 3000, 'error');

          if (turmaRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token inválido/expirado -> logout + redirect');
            logout();
            redirectToLogin('obterTurmaPorId: token');
            return;
          }

          debug.warn('falha obterTurmaPorId -> navigate /participante/page');
          navigate('/participante/page');
          return;
        }

        const turmaData = turmaRes.data;
        debug.table({
          turma_id: turmaData?.id,
          turma_nome: turmaData?.nome,
          status: turmaData?.status,
          data_limite: turmaData?.data_limite ?? turmaData?.dataLimite,
          idEmpresa: turmaData?.idEmpresa,
          idTrilha: turmaData?.idTrilha ?? turmaData?.id_trilha,
        });

        if (cancelled) return;

        setTurma(turmaData);

        // 2) Empresa (opcional)
        try {
          debug.time('obterEmpresaPorId');
          debug.log('REQ obterEmpresaPorId ->', { idEmpresa: turmaData?.idEmpresa });
          const empRes = await obterEmpresaPorId(turmaData.idEmpresa);
          debug.timeEnd('obterEmpresaPorId');
          debug.log('RES obterEmpresaPorId ->', empRes);

          if (empRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token na empresa -> logout + redirect');
            logout();
            redirectToLogin('obterEmpresaPorId: token');
            return;
          }

          if (empRes?.success && !cancelled) setEmpresa(empRes.data);
        } catch (e) {
          debug.warn('Erro ao buscar empresa (não bloqueia):', e);
        }

        // 3) Regras turma
        if (Number(turmaData?.status ?? 0) === 0) {
          debug.warn('turma inativa -> /participante/page');
          showToast('Essa turma ainda está inativa!', 4500, 'error');
          navigate('/participante/page');
          return;
        }

        const dataLimiteStr = turmaData?.data_limite || turmaData?.dataLimite;
        if (dataLimiteStr) {
          const hoje = new Date();
          const dataLimite = new Date(`${dataLimiteStr}T23:59:59`);
          debug.log('dataLimite check:', { hoje: hoje.toISOString(), dataLimite: dataLimite.toISOString() });

          if (hoje > dataLimite) {
            debug.warn('turma expirada -> /participante/page');
            showToast('O prazo para acessar essa turma expirou.', 4500, 'error');
            navigate('/participante/page');
            return;
          }
        }

        // 4) Vínculo participante x turma
        debug.time('listarParticipantesPorTurma');
        debug.log('REQ listarParticipantesPorTurma ->', { idTurma });
        const participantesRes = await listarParticipantesPorTurma(idTurma);
        debug.timeEnd('listarParticipantesPorTurma');
        debug.log('RES listarParticipantesPorTurma ->', participantesRes);

        if (!participantesRes?.success) {
          showToast(participantesRes?.message || 'Erro ao verificar vínculo do participante.', 3000, 'error');

          if (participantesRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token nos participantes -> logout + redirect');
            logout();
            redirectToLogin('listarParticipantesPorTurma: token');
            return;
          }

          debug.warn('falha listarParticipantesPorTurma -> /participante/page');
          navigate('/participante/page');
          return;
        }

        const vinculado = safeArray(participantesRes.data).some((p) => p?.id === usuario.id);
        debug.log('vinculado?', vinculado);

        if (!vinculado) {
          debug.time('associarParticipanteATurma');
          debug.log('REQ associarParticipanteATurma ->', { idParticipante: usuario.id, idTurma });
          const assocRes = await associarParticipanteATurma(usuario.id, idTurma);
          debug.timeEnd('associarParticipanteATurma');
          debug.log('RES associarParticipanteATurma ->', assocRes);

          if (!assocRes?.success) {
            showToast(assocRes?.message || 'Erro ao associar você à turma.', 3000, 'error');

            if (assocRes?.message?.toLowerCase?.().includes('token')) {
              debug.warn('token no associar -> logout + redirect');
              logout();
              redirectToLogin('associarParticipanteATurma: token');
              return;
            }

            debug.warn('falha associar -> /participante/page');
            navigate('/participante/page');
            return;
          }

          showToast('Vínculo com a turma realizado com sucesso!', 2500, 'success');
        }

        // 5) Trilha
        const idTrilha = turmaData?.idTrilha ?? turmaData?.id_trilha;
        if (!idTrilha) {
          debug.warn('turma sem idTrilha -> /participante/page');
          showToast('Esta turma não possui uma trilha vinculada.', 3500, 'warning');
          navigate('/participante/page');
          return;
        }

        debug.time('obterTrilhaPorId');
        debug.log('REQ obterTrilhaPorId ->', { idTrilha });
        const trilhaRes = await obterTrilhaPorId(idTrilha);
        debug.timeEnd('obterTrilhaPorId');
        debug.log('RES obterTrilhaPorId ->', trilhaRes);

        if (!trilhaRes?.success) {
          showToast(trilhaRes?.message || 'Erro ao carregar trilha.', 3000, 'error');

          if (trilhaRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token na trilha -> logout + redirect');
            logout();
            redirectToLogin('obterTrilhaPorId: token');
            return;
          }

          debug.warn('falha obterTrilhaPorId -> /participante/page');
          navigate('/participante/page');
          return;
        }

        if (cancelled) return;
        setTrilha(trilhaRes.data);

        // 6) Módulos
        debug.time('listarModulosPorTrilha');
        debug.log('REQ listarModulosPorTrilha ->', { idTrilha });
        const modRes = await listarModulosPorTrilha(idTrilha);
        debug.timeEnd('listarModulosPorTrilha');
        debug.log('RES listarModulosPorTrilha ->', modRes);

        if (!modRes?.success) {
          showToast(modRes?.message || 'Erro ao carregar módulos da trilha.', 3000, 'error');

          if (modRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token nos módulos -> logout + redirect');
            logout();
            redirectToLogin('listarModulosPorTrilha: token');
            return;
          }

          debug.warn('falha listarModulosPorTrilha -> /participante/page');
          navigate('/participante/page');
          return;
        }

        const lista = safeArray(modRes.data).slice().sort(sortByOrdem);
        debug.log('modulos (sorted) length:', lista.length);
        debug.table(lista.map(m => ({ id: m?.id, titulo: m?.titulo, ordem: m?.ordem, idTeste: m?.idTeste })));

        if (cancelled) return;

        setModulos(lista);

        // Reseta seleção/cache ao entrar na trilha
        setSelectedModuloId(null);
        setDetalhesCache({});
      } catch (e) {
        debug.error('catch fluxo principal:', e);
        showToast('Erro inesperado ao carregar a trilha.', 3000, 'error');
        navigate('/participante/page');
      } finally {
        debug.timeEnd('fluxo principal total');
        hideL();
        debug.groupEnd();
      }
    };

    carregar();

    return () => {
      cancelled = true;
      debug.log('cleanup efeito principal (cancelled=true)');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTurma, usuario?.id]);

  // ===== 2) Carrega detalhes do módulo selecionado =====
  useEffect(() => {
    let cancelled = false;

    const carregarDetalhesModulo = async () => {
      debug.groupCollapsed('carregarDetalhesModulo');
      debug.log('selectedModuloId:', selectedModuloId);

      if (!selectedModuloId) {
        debug.log('early return: selectedModuloId vazio');
        debug.groupEnd();
        return;
      }

      if (detalhesCache[selectedModuloId]) {
        debug.log('cache hit -> detalhes já carregados');
        debug.groupEnd();
        return;
      }

      const modulo = modulos.find((m) => String(m.id) === String(selectedModuloId));
      if (!modulo) {
        debug.warn('modulo não encontrado no state modulos');
        debug.groupEnd();
        return;
      }

      showL('Carregando módulo...');
      debug.time(`detalhes modulo ${selectedModuloId}`);

      try {
        // seções
        debug.time('listarSecoesPorModulo');
        debug.log('REQ listarSecoesPorModulo ->', { idModulo: modulo.id });
        const secoesRes = await listarSecoesPorModulo(modulo.id);
        debug.timeEnd('listarSecoesPorModulo');
        debug.log('RES listarSecoesPorModulo ->', secoesRes);

        if (!secoesRes?.success) {
          showToast(secoesRes?.message || 'Erro ao carregar seções do módulo.', 3000, 'error');

          if (secoesRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token nas seções -> logout + redirect');
            logout();
            redirectToLogin('listarSecoesPorModulo: token');
            return;
          }

          return;
        }

        const secoes = safeArray(secoesRes.data).slice().sort(sortByOrdem);
        debug.log('secoes length:', secoes.length);
        debug.table(secoes.map(s => ({ id: s?.id, titulo: s?.titulo, ordem: s?.ordem })));

        // conteúdos (paralelo)
        debug.time('listarConteudosPorSecao (Promise.all)');
        const conteudosEntries = await Promise.all(
          secoes.map(async (secao) => {
            try {
              debug.log('REQ listarConteudosPorSecao ->', { idSecao: secao.id });
              const contRes = await listarConteudosPorSecao(secao.id);
              debug.log('RES listarConteudosPorSecao ->', { idSecao: secao.id, success: contRes?.success, message: contRes?.message });

              if (!contRes?.success) return [secao.id, []];

              const conteudos = safeArray(contRes.data).slice().sort(sortByOrdem);
              return [secao.id, conteudos];
            } catch (e) {
              debug.warn('Erro listarConteudosPorSecao:', { secaoId: secao?.id, e });
              return [secao.id, []];
            }
          })
        );
        debug.timeEnd('listarConteudosPorSecao (Promise.all)');

        const conteudosBySecao = Object.fromEntries(conteudosEntries);
        debug.log('conteudosBySecao keys:', Object.keys(conteudosBySecao));

        // teste externo (opcional)
        let testeTitulo = '';
        if (modulo?.idTeste) {
          try {
            debug.time('obterTestePorId (modulo.idTeste)');
            debug.log('REQ obterTestePorId ->', { idTeste: modulo.idTeste });
            const testeRes = await obterTestePorId(modulo.idTeste);
            debug.timeEnd('obterTestePorId (modulo.idTeste)');
            debug.log('RES obterTestePorId ->', testeRes);

            if (testeRes?.success) testeTitulo = testeRes?.data?.nome || '';
          } catch (e) {
            debug.warn('Erro ao obterTestePorId (não bloqueia):', e);
          }
        }

        if (cancelled) return;

        setDetalhesCache((prev) => ({
          ...prev,
          [selectedModuloId]: { secoes, conteudosBySecao, testeTitulo },
        }));
      } catch (e) {
        debug.error('catch detalhes modulo:', e);
        showToast('Erro inesperado ao carregar módulo.', 3000, 'error');
      } finally {
        debug.timeEnd(`detalhes modulo ${selectedModuloId}`);
        hideL();
        debug.groupEnd();
      }
    };

    carregarDetalhesModulo();

    return () => {
      cancelled = true;
      debug.log('cleanup efeito detalhes (cancelled=true)');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModuloId, modulos, detalhesCache]);

  // ===== Render do conteúdo (com prefixo + PDF viewer) =====
  const renderConteudo = (c) => {
    const tipo = normalizeText(c?.tipo).toLowerCase();
    const titulo = normalizeText(c?.titulo);
    const texto = normalizeText(c?.texto);

    const urlImagemRaw = normalizeText(c?.url_imagem);
    const urlLinkRaw = normalizeText(c?.url_link);
    const urlVideo = normalizeText(c?.url_video);

    // Imagem / PDF (prefixo)
    const urlImagem = urlImagemRaw ? toAssetUrl(urlImagemRaw) : '';
    const urlLink = urlLinkRaw ? (isAbsoluteLike(urlLinkRaw) ? urlLinkRaw : toAssetUrl(urlLinkRaw)) : '';

    // Detecta PDF tanto no tipo quanto nas URLs
    const isPdf =
      tipo === 'pdf' ||
      looksLikePdf(urlLinkRaw) ||
      looksLikePdf(urlImagemRaw);

    // Lista
    let listaItens = c?.lista_itens;
    if (typeof listaItens === 'string') {
      try {
        const parsed = JSON.parse(listaItens);
        if (Array.isArray(parsed)) listaItens = parsed;
      } catch {
        // ignore
      }
    }
    listaItens = safeArray(listaItens);

    const embedUrl = urlVideo ? toYouTubeEmbedUrl(urlVideo) : null;

    // Se o PDF veio em url_imagem (sim, já vi acontecer), prioriza isso
    const pdfUrlFinal = (() => {
      const a = urlLinkRaw || '';
      const b = urlImagemRaw || '';
      if (looksLikePdf(a)) return toAssetUrl(a);
      if (looksLikePdf(b)) return toAssetUrl(b);
      // fallback: se isPdf mas não “parece” pdf, tenta pelo link
      return urlLink || urlImagem || '';
    })();

    if (DEBUG) {
      debug.log('renderConteudo', {
        id: c?.id,
        tipo,
        titulo,
        urlImagemRaw,
        urlImagem,
        urlLinkRaw,
        urlLink,
        isPdf,
        pdfUrlFinal,
      });
    }

    return (
      <div key={c.id} className={styles.conteudoItem}>
        {titulo ? <div className={styles.conteudoTitulo}>{titulo}</div> : null}
        {texto ? <div className={styles.conteudoTexto}>{texto}</div> : null}

        {listaItens.length > 0 ? (
          <ul className={styles.conteudoLista}>
            {listaItens.map((it, idx) => (
              <li key={`${c.id}-li-${idx}`}>{String(it)}</li>
            ))}
          </ul>
        ) : null}

        {/* ✅ PDF: botão para abrir no modal */}
        {isPdf && pdfUrlFinal ? (
          <div className={styles.pdfBox}>
            <button
              type="button"
              className={styles.pdfOpenBtn}
              onClick={() => openPdf(pdfUrlFinal, titulo || 'Documento PDF')}
            >
              Visualizar PDF
            </button>
          </div>
        ) : null}

        {/* ✅ Imagem (prefixada) */}
        {!isPdf && urlImagem ? (
          <img
            src={urlImagem}
            alt={titulo || 'Imagem do conteúdo'}
            className={styles.conteudoImagem}
            onError={() => debug.warn('Falha ao carregar imagem:', urlImagem)}
          />
        ) : null}

        {/* ✅ Link (prefixado quando relativo) */}
        {!isPdf && urlLink ? (
          <a
            href={urlLink}
            target="_blank"
            rel="noreferrer"
            className={styles.conteudoLink}
          >
            Acessar link
          </a>
        ) : null}

        {/* ✅ Vídeo */}
        {urlVideo ? (
          embedUrl ? (
            <div className={styles.videoWrapper}>
              <iframe
                title={titulo || `video-${c.id}`}
                src={embedUrl}
                className={styles.videoFrame}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className={styles.mutedNote}>
              Link do vídeo inválido. Verifique a URL do YouTube.
            </div>
          )
        ) : null}

        {tipo && !texto && !listaItens.length && !urlImagemRaw && !urlLinkRaw && !urlVideo ? (
          <div className={styles.mutedNote}>Conteúdo do tipo “{tipo}”</div>
        ) : null}
      </div>
    );
  };

  // ✅ Loading fallback simples
  if (!turma || !trilha) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.containerTop}>
          <h1 className={styles.title}>Carregando trilha...</h1>
          {DEBUG && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#777' }}>
              <div><strong>idTurma:</strong> {String(idTurma || '(vazio)')}</div>
              <div><strong>usuario.id:</strong> {String(usuario?.id || '(vazio)')}</div>
              <div><strong>loaderCount:</strong> {String(loaderCountRef.current)}</div>
              <div><strong>IMG_PATH:</strong> {String(URLS?.IMG_PATH || '(vazio)')}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      {/* ✅ Modal PDF */}
      <PdfViewerModal
        open={pdfViewer.open}
        title={pdfViewer.title}
        url={pdfViewer.url}
        onClose={closePdf}
      />

      <div className={styles.containerTop}>
        <h1 className={styles.title}>{trilha?.titulo ? trilha.titulo : 'Trilha'}</h1>

        <div className={styles.mutedNote}>
          {empresa?.nome ? `${empresa.nome} • ` : ''}
          {turma?.nome ? turma.nome : ''}
        </div>

        {trilha?.descricao ? <div className={styles.subTitle}>{trilha.descricao}</div> : null}

        {selectedModuloId ? (
          <div className={styles.navigationArea}>
            <button
              type="button"
              className={styles.navBackButton}
              onClick={() => setSelectedModuloId(null)}
            >
              Voltar para módulos
            </button>
          </div>
        ) : null}
      </div>

      {!selectedModuloId && (
        <div className={styles.formulario}>
          {modulos.length === 0 ? (
            <div className={styles.perguntaWrapper}>
              <div className={styles.pergunta}>Nenhum módulo disponível</div>
              <div className={styles.mutedNote}>
                Esta trilha ainda não possui módulos cadastrados.
              </div>
            </div>
          ) : (
            <div className={styles.modulosGrid}>
              {modulos.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={styles.moduloCard}
                  onClick={() => setSelectedModuloId(m.id)}
                  title={m?.descricao || ''}
                >
                  <div className={styles.moduloCardTitle}>{m.titulo}</div>
                  {m?.descricao ? <div className={styles.moduloCardDesc}>{m.descricao}</div> : null}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedModuloId && selectedModulo && (
        <div className={styles.formulario}>
          <div className={styles.perguntaWrapper}>
            <div className={styles.pergunta}>{selectedModulo.titulo}</div>

            {selectedModulo?.descricao ? (
              <div className={styles.mutedNote}>{selectedModulo.descricao}</div>
            ) : null}

            {selectedModulo?.idTeste ? (
              <div className={styles.testeBox}>
                <div className={styles.testeTitle}>
                  {detalhesSelecionado?.testeTitulo ? detalhesSelecionado.testeTitulo : 'Teste externo'}
                </div>

                <Link to={`/res/${idTurma}`} className={styles.testeLink}>
                  Responder ao teste externo
                </Link>
              </div>
            ) : null}
          </div>

          {(detalhesSelecionado?.secoes || []).map((secao) => {
            const conteudos = detalhesSelecionado?.conteudosBySecao?.[secao.id] || [];

            return (
              <div key={secao.id} className={styles.perguntaWrapper}>
                <div className={styles.pergunta}>{secao.titulo}</div>

                {secao?.descricao ? <div className={styles.mutedNote}>{secao.descricao}</div> : null}

                <div className={styles.opcoesWrapper}>
                  {conteudos.length === 0 ? (
                    <div className={styles.mutedNote}>Nenhum conteúdo cadastrado nesta seção.</div>
                  ) : (
                    conteudos.map(renderConteudo)
                  )}
                </div>
              </div>
            );
          })}

          {detalhesSelecionado && detalhesSelecionado.secoes?.length === 0 ? (
            <div className={styles.perguntaWrapper}>
              <div className={styles.pergunta}>Sem seções</div>
              <div className={styles.mutedNote}>Este módulo ainda não possui seções cadastradas.</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default EadTrilhaPage;